import { useMemo } from 'react';

import { cx } from '@/utils/cx';

import type {
  TriggerButtonDescriptor,
  TriggerButtonTarget,
} from './ActionOverview.helpers';
import { TriggerActionButton } from './TriggerActionButton';
import { TriggerOverflowButton } from './TriggerOverflowButton';

/**
 * How many "View X" buttons render inline before the rest collapse behind a
 * "+N" overflow menu. Keeps the detail header row from crowding out the
 * title when a trigger has many participants.
 */
const MAX_VISIBLE_BUTTONS = 3;

interface TriggerShortcutButtonsProps {
  isLoading: boolean;
  buttons: TriggerButtonDescriptor[];
  orgId: number | undefined;
  onActivate: (target: TriggerButtonTarget) => void;
  /** Layout override — the detail header renders a single non-wrapping row. */
  className?: string;
}

/**
 * Row of "View X" trigger shortcut buttons, or a loading skeleton while the
 * trigger fetch that derives them is in flight. Renders nothing once loading
 * is done and there are no buttons to show. The first {@link
 * MAX_VISIBLE_BUTTONS} render inline (already priority-ordered by the hook:
 * deal > contacts > companies > everything else); the rest collapse behind a
 * "+N" dropdown.
 */
export function TriggerShortcutButtons({
  isLoading,
  buttons,
  orgId,
  onActivate,
  className,
}: TriggerShortcutButtonsProps) {
  const visibleButtons = useMemo(
    () => buttons.slice(0, MAX_VISIBLE_BUTTONS),
    [buttons]
  );
  const overflowButtons = useMemo(
    () => buttons.slice(MAX_VISIBLE_BUTTONS),
    [buttons]
  );

  if (!isLoading && buttons.length === 0) {
    return null;
  }

  return (
    <div
      className={cx(
        'flex gap-2',
        className ?? 'pointer-events-auto flex-wrap pt-3'
      )}
    >
      {isLoading ? (
        <>
          <div
            aria-hidden
            className='h-7 w-28 animate-pulse rounded-lg bg-secondary'
          />
          <div
            aria-hidden
            className='h-7 w-24 animate-pulse rounded-lg bg-secondary'
          />
        </>
      ) : (
        <>
          {visibleButtons.map(descriptor => (
            <TriggerActionButton
              key={descriptor.key}
              descriptor={descriptor}
              orgId={orgId}
              onActivate={onActivate}
            />
          ))}
          {overflowButtons.length > 0 && (
            <TriggerOverflowButton
              overflowButtons={overflowButtons}
              onActivate={onActivate}
            />
          )}
        </>
      )}
    </div>
  );
}
